// Onglet « Proposition TRAME » de la conception d'un dossier : Préparer (tracer pièces et éléments), Proposition (plan annoté de
// la variante choisie), Comparer (critères, besoin par pièce, plans côte à côte). La proposition est un calcul DÉTERMINISTE
// d'avant-projet : aucune mention d'IA (une mention fausse est aussi une faute) ; rappel du bilan thermique à confirmer.
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Calculator, FileImage, Settings2 } from 'lucide-react';
import { api, ErreurApi, messageErreur } from '../../../lib/api.ts';
import { useSession } from '../../../lib/session.tsx';
import { dateHeure } from '../../../lib/format.ts';
import { Alerte, Bouton, Chargement, ChampChoix, Dialogue, EtatErreur, EtatVide, LienBouton, Panneau, useNotifier } from '../../../components/ui.tsx';
import { LIBELLES_STRATEGIE, MENTION_BESOIN, MENTION_PROPOSITION, STRATEGIES, type Strategie } from '@shared/proposition/types.ts';
import { Preparer } from './Preparer.tsx';
import { Visuel } from './Visuel.tsx';
import { Comparaison } from './Comparaison.tsx';
import { BoutonPdfPlan } from './ExportPdf.tsx';
import { TOUS_CALQUES } from './Visuel.tsx';
import type { Calque } from './CalquesVariante.tsx';
import { useCalculs, usePiecesElements, usePlans, useRafraichir } from './donnees.ts';
import { messageCalcul, pointsManquants, type CalculVue } from './logique.ts';
import '../../../styles/proposition.css';

type Etape = 'preparer' | 'visuel' | 'comparer';
const ETAPES: { id: Etape; libelle: string }[] = [{ id: 'preparer', libelle: 'Préparer' }, { id: 'visuel', libelle: 'Proposition' }, { id: 'comparer', libelle: 'Comparer' }];
interface Options { strategies: Strategie[]; gamme: string; avecTechnicien: boolean }

/** Bandeau permanent : « Proposé par TRAME — à valider », bilan thermique à confirmer, origine du calcul (version, date, empreinte). */
function Bandeau({ calcul, perimee }: { calcul: CalculVue; perimee: boolean }) {
  const autres = calcul.mentions.filter((m) => m !== MENTION_PROPOSITION && m !== MENTION_BESOIN);
  return (
    <div className="prp-bandeau" role="note" aria-label="Statut de la proposition">
      <p className="petit"><strong>Proposé par TRAME — à valider.</strong> <span className="prp-long">{MENTION_PROPOSITION.replace(/^Proposé par TRAME — à valider\.\s*/, '')}</span>
        <span className="prp-court">Pré-dimensionnement à confirmer par un bilan thermique.</span>
        <span className="prp-long tres-petit"> {MENTION_BESOIN}</span></p>
      <details className="tres-petit">
        <summary>Mentions et origine du calcul</summary>
        <p className="prp-court">{MENTION_PROPOSITION}</p>
        <p className="prp-court">{MENTION_BESOIN}</p>
        {autres.map((m) => <p key={m}>{m}</p>)}
        <p>Moteur <span className="prp-mono">{calcul.versionMoteur}</span> · calculé le {dateHeure(calcul.calculeLe)} · empreinte des entrées <span className="prp-mono">{calcul.empreinte}</span>
          {calcul.entreesValidees ? '' : ' · entrées non validées (calcul pour information)'}</p>
      </details>
      {perimee && <p className="prp-perimee petit" role="alert"><strong>Entrées modifiées depuis le calcul — relancer.</strong></p>}
    </div>
  );
}

export function OngletProposition({ dossierId }: { dossierId: string }) {
  const { peut } = useSession();
  const calculer = peut('proposition.calculer');
  const notifier = useNotifier();
  const [params, setParams] = useSearchParams();
  const plansQ = usePlans(dossierId);
  const plans = plansQ.data?.plans ?? [];
  const pe = usePiecesElements(plans);
  const calculsQ = useCalculs(dossierId);
  const rafraichir = useRafraichir(dossierId);
  const calcul = calculsQ.data?.calculs[0] ?? null;
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<{ titre: string; texte: string; cheminements?: boolean } | null>(null);
  const [voirOptions, setVoirOptions] = useState(false);
  const [options, setOptions] = useState<Options | null>(null);
  const [calques, setCalques] = useState<Set<Calque>>(TOUS_CALQUES);
  const modeles = useQuery({ queryKey: ['modeles-cvc'], enabled: calculer, queryFn: () => api.get<{ modeles: { fabricant: string; gamme: string; role: string; actif?: boolean }[] }>('/api/conception/modeles-cvc') });
  const gammes = useMemo(() => [...new Set((modeles.data?.modeles ?? []).filter((m) => m.actif !== false && (m.role === 'ue' || m.role === 'ui')).map((m) => `${m.fabricant}|${m.gamme}`))], [modeles.data]);
  useEffect(() => {
    if (options || !calculsQ.isSuccess) return;
    const s = (calcul?.strategies ?? ['mixte_usage', 'cassettes', 'gainables']).filter((x): x is Strategie => (STRATEGIES as readonly string[]).includes(x)).slice(0, 3);
    setOptions({ strategies: s.length ? s : ['mixte_usage', 'cassettes'], gamme: '', avecTechnicien: true });
  }, [calculsQ.isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const etapeUrl = params.get('etape') as Etape | null;
  const etape: Etape = etapeUrl && ETAPES.some((e) => e.id === etapeUrl) ? etapeUrl : calcul ? 'visuel' : 'preparer';
  const changer = (n: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(n)) { if (v == null) p.delete(k); else p.set(k, v); }
    setParams(p, { replace: true });
  };
  const varianteId = calcul?.variantes.some((v) => v.id === params.get('variante')) ? params.get('variante')! : calcul?.variantes[0]?.id ?? '';

  if (plansQ.isLoading || calculsQ.isLoading) return <Chargement lignes={4} libelle="Chargement de la proposition…" />;
  if (plansQ.isError) return <EtatErreur erreur={plansQ.error} reessayer={() => plansQ.refetch()} />;
  if (calculsQ.isError) return <EtatErreur erreur={calculsQ.error} reessayer={() => calculsQ.refetch()} />;
  if (!plans.length) {
    return (
      <Panneau>
        <EtatVide icone={<FileImage />} titre="Aucun plan pour ce dossier" texte="La proposition se construit sur un plan à l’échelle validée : créez le plan et calibrez son échelle dans l’onglet Cheminements."
          action={<LienBouton variante="principal" to={`/etudes/${dossierId}/conception?vue=cheminements`}>Ouvrir les cheminements</LienBouton>} />
      </Panneau>
    );
  }

  const manques = pointsManquants(plans.map((p) => ({ id: p.id, nom: p.nom, echelleValidee: pe.echelles.get(p.id) ?? false })), pe.pieces, pe.elements);
  const bloque = manques.some((m) => m.niveau === 'bloquant');
  const perimee = !!calcul && calcul.variantes.some((v) => v.statut === 'perimee');

  const lancer = async () => {
    if (!options) return;
    setEnCours(true); setErreur(null);
    const [fabricant, gamme] = options.gamme ? options.gamme.split('|') : [null, null];
    try {
      const r = await api.post<{ runId: string; variantes: { id: string }[] }>(`/api/dossiers/${dossierId}/proposition`,
        { strategies: options.strategies, fabricant, gamme, avecTechnicien: options.avecTechnicien });
      await rafraichir();
      notifier(`Proposition calculée : ${r.variantes.length} variante(s) classée(s).`);
      changer({ etape: 'visuel', variante: r.variantes[0]?.id ?? null });
    } catch (e) {
      const m = messageCalcul(e instanceof ErreurApi ? e.code : undefined, messageErreur(e));
      setErreur(m);
      if (e instanceof ErreurApi && e.code === 'donnees_invalides' && /gamme/i.test(e.message)) setVoirOptions(true);
    } finally { setEnCours(false); }
  };
  const resumeOptions = options ? `${options.strategies.map((s) => LIBELLES_STRATEGIE[s]).join(', ')}${options.avecTechnicien ? ' + proposition du technicien' : ''}` : '';

  return (
    <div className="pile prp-page">
      <div className="prp-entete">
        <div className="segment prp-etapes" role="group" aria-label="Étape de la proposition">
          {ETAPES.map((e) => <button key={e.id} type="button" aria-pressed={etape === e.id} disabled={e.id !== 'preparer' && !calcul}
            onClick={() => changer({ etape: e.id })}>{e.libelle}</button>)}
        </div>
        {calcul && etape !== 'preparer' && (
          <BoutonPdfPlan dossierId={dossierId} variantes={calcul.variantes} varianteId={varianteId}
            calques={etape === 'visuel' ? calques : undefined} comparaison={etape === 'comparer'} />
        )}
      </div>

      {erreur && (
        <Alerte ton="danger" titre={erreur.titre} action={erreur.cheminements ? <LienBouton compact to={`/etudes/${dossierId}/conception?vue=cheminements`}>Cheminements</LienBouton> : undefined}>{erreur.texte}</Alerte>
      )}
      {enCours && <p className="petit prp-calcul-en-cours" role="status" aria-live="polite">Calcul en cours : besoins, choix des unités, implantation, réseau, diamètres et contrôles…</p>}
      {calcul && etape !== 'preparer' && <Bandeau calcul={calcul} perimee={perimee} />}

      {pe.erreur ? <EtatErreur erreur={pe.erreur} /> : pe.chargement ? <Chargement lignes={3} /> : etape === 'preparer' ? (
        <Preparer dossierId={dossierId} plans={plans} pieces={pe.pieces} elements={pe.elements} echelles={pe.echelles} pointsEntrees={calcul?.pointsEntrees ?? []} rafraichir={rafraichir} />
      ) : !calcul ? null : etape === 'visuel' ? (
        <Visuel dossierId={dossierId} calcul={calcul} plans={plans} pieces={pe.pieces} varianteId={varianteId} rafraichir={rafraichir} actifs={calques} setActifs={setCalques}
          choisirVariante={(id) => changer({ variante: id })} />
      ) : (
        <Comparaison dossierId={dossierId} calcul={calcul} plans={plans} pieces={pe.pieces} ouvrirVariante={(id) => changer({ etape: 'visuel', variante: id })} />
      )}

      {(calculer || !calcul) && (
        <div className="barre-actions barre-collante prp-barre">
          <span className="barre-collante-resume tres-petit">
            {calcul ? `Dernier calcul : ${dateHeure(calcul.calculeLe)} · ${calcul.variantes.length} variante(s)` : 'Aucun calcul pour ce dossier.'}
            {calculer && options ? <span className="masque-etroit"> · {resumeOptions}</span> : null}
            {!calculer ? ' Le calcul est réservé au référent technique et au chargé d’affaires.' : bloque ? ' Données bloquantes : voir « Avant de calculer ».' : ''}
          </span>
          {calculer && <Bouton icone={<Settings2 aria-hidden />} onClick={() => setVoirOptions(true)} aria-label="Options du calcul" className="btn-icone-etroit">
            <span className="masque-etroit">Options</span></Bouton>}
          {calculer && <Bouton variante="principal" icone={<Calculator aria-hidden />} chargement={enCours} onClick={lancer}>{calcul ? 'Recalculer la proposition' : 'Calculer la proposition'}</Bouton>}
        </div>
      )}

      {options && (
        <Dialogue titre="Options du calcul" ouvert={voirOptions} fermer={() => setVoirOptions(false)}
          pied={<><Bouton onClick={() => setVoirOptions(false)}>Fermer</Bouton><Bouton variante="principal" icone={<Calculator aria-hidden />} chargement={enCours}
            disabled={!options.strategies.length} onClick={() => { setVoirOptions(false); void lancer(); }}>Calculer la proposition</Bouton></>}>
          <div className="pile">
            <fieldset className="prp-groupe"><legend>Variantes à calculer (1 à 3)</legend>
              {STRATEGIES.map((s) => (
                <label key={s} className="prp-case"><input type="checkbox" checked={options.strategies.includes(s)}
                  disabled={!options.strategies.includes(s) && options.strategies.length >= 3}
                  onChange={(e) => setOptions({ ...options, strategies: e.target.checked ? [...options.strategies, s] : options.strategies.filter((x) => x !== s) })} />
                  {LIBELLES_STRATEGIE[s]}</label>
              ))}
            </fieldset>
            {gammes.length > 1 && <ChampChoix libelle="Gamme du catalogue" value={options.gamme} onChange={(e) => setOptions({ ...options, gamme: e.target.value })}
              options={[{ valeur: '', libelle: 'À choisir' }, ...gammes.map((g) => ({ valeur: g, libelle: g.replace('|', ' — ') }))]} aide="Obligatoire quand le catalogue compte plusieurs gammes." />}
            <label className="prp-case"><input type="checkbox" checked={options.avecTechnicien} onChange={(e) => setOptions({ ...options, avecTechnicien: e.target.checked })} />
              Évaluer aussi la proposition du technicien (repères et liaisons déjà tracés), sur les mêmes critères</label>
            <p className="petit secondaire">Matériels de la gamme, tables de la notice et réglages de l’entreprise : <Link to="/etudes/catalogue-technique">Catalogue technique et réglages</Link>.</p>
          </div>
        </Dialogue>
      )}
    </div>
  );
}
